import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const money = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const PerformanceTab = ({ events }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState('');
  const [location, setLocation] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const types = useMemo(() => Array.from(new Set(events.map(e => e.event_type).filter(Boolean))), [events]);
  const locations = useMemo(() => Array.from(new Set(events.map(e => e.location).filter(Boolean))), [events]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sample-tracker/performance', {
        params: { event_type: eventType || undefined, location: location || undefined, include_archived: includeArchived },
      });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load performance'); }
    finally { setLoading(false); }
  }, [eventType, location, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const downloadReport = async (format) => {
    try {
      const res = await api.get('/sample-tracker/performance/export', {
        params: { format, event_type: eventType || undefined, location: location || undefined, include_archived: includeArchived },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'pdf' ? 'sample_performance.pdf' : 'sample_performance.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-semibold text-gray-600 block">Event Type</label>
            <select value={eventType} onChange={e => setEventType(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="perf-type-filter">
              <option value="">All</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block">Location</label>
            <select value={location} onChange={e => setLocation(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="perf-location-filter">
              <option value="">All</option>
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} className="w-4 h-4" data-testid="perf-archived-toggle" />
            Include archived
          </label>
          <Button variant="outline" onClick={() => downloadReport('xlsx')} className="ml-auto" data-testid="perf-export-xlsx">Excel</Button>
          <Button variant="outline" onClick={() => downloadReport('pdf')} data-testid="perf-export-pdf">PDF</Button>
        </CardContent>
      </Card>

      {loading ? <p className="text-sm text-gray-400 px-1">Loading…</p> : data && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm border-collapse" data-testid="performance-grid">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-3 py-2 font-semibold sticky left-0 bg-gray-50 z-20 min-w-[120px]">Design</th>
                    <th className="px-2 py-2 font-semibold text-right">Sent</th>
                    <th className="px-2 py-2 font-semibold text-right">Conv %</th>
                    <th className="px-2 py-2 font-semibold text-right">Orders</th>
                    <th className="px-2 py-2 font-semibold text-right">Qty</th>
                    <th className="px-2 py-2 font-semibold text-right">Amount</th>
                    {data.events.map(e => (
                      <th key={e.id} className="px-2 py-2 font-semibold text-center min-w-[90px]" title={`${e.event_type || ''} ${e.location || ''}`}>{e.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.design_code} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`perf-row-${r.design_code}`}>
                      <td className="px-3 py-2 font-medium text-gray-800 sticky left-0 bg-white z-10">{r.design_code}</td>
                      <td className="px-2 py-2 text-right text-gray-600">{r.sent_count}</td>
                      <td className="px-2 py-2 text-right text-gray-600">{r.conversion_pct != null ? `${r.conversion_pct}%` : '—'}</td>
                      <td className="px-2 py-2 text-right text-gray-600">{r.total_orders}</td>
                      <td className="px-2 py-2 text-right font-semibold">{r.total_qty}</td>
                      <td className="px-2 py-2 text-right text-gray-600">{money(r.total_amount)}</td>
                      {r.cells.map(c => (
                        <td key={c.event_id} className="px-2 py-2 text-center">
                          <div className="flex flex-col items-center">
                            {c.sent
                              ? <span className="text-emerald-600 text-xs" title="Carried as sample">●</span>
                              : <span className="text-gray-200 text-xs" title="Not carried">○</span>}
                            {c.qty > 0 && <span className={`text-xs ${c.sent ? 'text-gray-700' : 'text-orange-600'}`}>{c.qty}</span>}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {data.rows.length === 0 && <tr><td colSpan={6 + data.events.length} className="px-3 py-6 text-center text-gray-400">No data — allocate samples or tag orders to events first</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <p className="text-xs text-gray-400">● carried as sample · ○ not carried · orange qty = ordered at an event where it was <strong>not</strong> carried.</p>
    </div>
  );
};

export default PerformanceTab;
